import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useRecipeHistory } from '../../hooks/useRecipeHistory';
import { RecipeData } from '../../types';
import './styles.css';

interface HistoryDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectRecipe: (recipe: RecipeData) => void;
  refreshRecipeTrigger?: number; // 新增：刷新触发器
}

type FilterType = 'all' | 'recent' | 'favorites';

const HistoryDrawer: React.FC<HistoryDrawerProps> = ({
  isOpen,
  onClose,
  onSelectRecipe,
  refreshRecipeTrigger
}) => {
  const { recipes, loading, error, refreshHistory, deleteRecipe } = useRecipeHistory(refreshRecipeTrigger);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState<FilterType>('all');
  const [isClosing, setIsClosing] = useState(false);

  // 滚动位置管理
  const [scrollPosition, setScrollPosition] = useState(0);

  // 优化格式化函数 - 使用useCallback避免重复计算
  const _formatDate = useCallback((dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 1) {
      return '今天';
    } else if (diffDays === 2) {
      return '昨天';
    } else if (diffDays <= 7) {
      return `${diffDays} 天前`;
    } else {
      return date.toLocaleDateString('zh-CN', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    }
  }, []);

  const _formatDetailTime = useCallback((dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  }, []);

  // 使用节流优化搜索性能
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState(searchQuery);
  
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);
    
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // 优化过滤逻辑
  const filteredRecipes = useMemo(() => {
    let filtered = [...recipes];

    // 应用搜索过滤
    if (debouncedSearchQuery.trim()) {
      const query = debouncedSearchQuery.toLowerCase().trim();
      filtered = filtered.filter(recipe =>
        recipe.name.toLowerCase().includes(query) ||
        (recipe.description && recipe.description.toLowerCase().includes(query))
      );
    }

    // 应用类型过滤
    switch (filterType) {
      case 'recent':
        const sevenDaysAgo = new Date();
        sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
        filtered = filtered.filter(recipe => 
          new Date(recipe.createdAt) >= sevenDaysAgo
        );
        break;
      case 'favorites':
        filtered = filtered.filter(recipe => recipe.chatHistory.length >= 10);
        break;
      case 'all':
      default:
        break;
    }

    return filtered.sort((a, b) => 
      new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
  }, [recipes, debouncedSearchQuery, filterType]);

  // 使用requestAnimationFrame优化动画
  const _handleRecipeClick = useCallback((recipe: RecipeData) => {
    setIsClosing(true);
    
    // 使用requestAnimationFrame确保动画流畅
    requestAnimationFrame(() => {
      setTimeout(() => {
        onSelectRecipe(recipe);
        setIsClosing(false);
      }, 300); // 匹配CSS动画时长
    });
  }, [onSelectRecipe]);

  const _handleClose = useCallback(() => {
    setIsClosing(true);
    
    // 使用requestAnimationFrame优化关闭动画
    requestAnimationFrame(() => {
      setTimeout(() => {
        onClose();
        setIsClosing(false);
        setSearchQuery('');
        setFilterType('all');
      }, 300); // 匹配CSS动画时长
    });
  }, [onClose]);

  // 优化事件处理器
  const _handleDeleteRecipe = useCallback(async (recipeId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    
    if (window.confirm('确定要删除这个配方吗？此操作不可撤销。')) {
      try {
        await deleteRecipe(recipeId);
      } catch (error) {
        alert('删除失败，请重试');
        console.error('删除配方失败:', error);
      }
    }
  }, [deleteRecipe]);

  const _handleBackdropClick = useCallback((event: React.MouseEvent) => {
    if (event.target === event.currentTarget) {
      _handleClose();
    }
  }, [_handleClose]);

  const _handleKeyDown = useCallback((event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      _handleClose();
    }
  }, [_handleClose]);

  const _handleSearchChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  }, []);

  const _handleFilterChange = useCallback((type: FilterType) => {
    setFilterType(type);
  }, []);

  const _handleExportAll = () => {
    // 导出所有配方的功能
    const dataStr = JSON.stringify(recipes, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `配方历史_${new Date().toISOString().split('T')[0]}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const _handleClearAll = async () => {
    if (window.confirm('确定要清空所有历史配方吗？此操作不可撤销。')) {
      try {
        // 批量删除功能
        for (const recipe of recipes) {
          await deleteRecipe(recipe.id);
        }
      } catch (error) {
        alert('清空失败，请重试');
        console.error('清空历史失败:', error);
      }
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div 
      className={`history-drawer-overlay ${isClosing ? 'closing' : ''}`}
      onClick={_handleBackdropClick}
      onKeyDown={_handleKeyDown}
      role="dialog"
      aria-modal="true"
      aria-labelledby="drawer-title"
      tabIndex={-1}
    >
      <div className={`history-drawer-container ${isClosing ? 'closing' : ''}`}>
        {/* 头部 */}
        <div className="history-drawer-header">
          <div>
            <h2 id="drawer-title" className="history-drawer-title">
              <span className="history-drawer-title-icon">📚</span>
              历史配方
            </h2>
            <p className="history-drawer-subtitle">
              共 {recipes.length} 个配方，筛选后 {filteredRecipes.length} 个
            </p>
          </div>
          <button 
            className="history-drawer-close"
            onClick={_handleClose}
            type="button"
            aria-label="关闭历史抽屉"
          >
            ✕
          </button>
        </div>

        {/* 搜索和筛选 */}
        <div className="history-drawer-filters">
          <div className="history-drawer-search">
            <span className="history-drawer-search-icon">🔍</span>
            <input
              type="text"
              className="history-drawer-search-input"
              placeholder="搜索配方名称或描述..."
              value={searchQuery}
              onChange={_handleSearchChange}
            />
          </div>
          
          <div className="history-drawer-filter-tabs">
            <button
              className={`history-drawer-filter-tab ${filterType === 'all' ? 'active' : ''}`}
              onClick={() => _handleFilterChange('all')}
            >
              全部
            </button>
            <button
              className={`history-drawer-filter-tab ${filterType === 'recent' ? 'active' : ''}`}
              onClick={() => _handleFilterChange('recent')}
            >
              最近
            </button>
            <button
              className={`history-drawer-filter-tab ${filterType === 'favorites' ? 'active' : ''}`}
              onClick={() => _handleFilterChange('favorites')}
            >
              常用
            </button>
          </div>
        </div>

        {/* 内容区域 */}
        <div className="history-drawer-content">
          {loading ? (
            <div className="history-drawer-loading">
              <div className="history-drawer-loading-spinner"></div>
              <span className="history-drawer-loading-text">加载中...</span>
            </div>
          ) : error ? (
            <div className="history-drawer-empty">
              <div className="history-drawer-empty-icon">❌</div>
              <h3 className="history-drawer-empty-title">加载失败</h3>
              <p className="history-drawer-empty-description">{error}</p>
              <button 
                className="history-drawer-footer-btn primary"
                onClick={refreshHistory}
                type="button"
              >
                重试
              </button>
            </div>
          ) : filteredRecipes.length === 0 ? (
            <div className="history-drawer-empty">
              <div className="history-drawer-empty-icon">
                {searchQuery ? '🔍' : '📝'}
              </div>
              <h3 className="history-drawer-empty-title">
                {searchQuery ? '未找到匹配的配方' : '暂无配方历史'}
              </h3>
              <p className="history-drawer-empty-description">
                {searchQuery 
                  ? '尝试调整搜索关键词或筛选条件'
                  : '开始创建您的第一个配方吧！'
                }
              </p>
            </div>
          ) : (
            <div className="history-drawer-list">
              {filteredRecipes.map((recipe) => (
                <div
                  key={recipe.id}
                  className="history-drawer-item"
                  onClick={() => _handleRecipeClick(recipe)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      _handleRecipeClick(recipe);
                    }
                  }}
                >
                  <div className="history-drawer-item-header">
                    <h4 className="history-drawer-item-title">{recipe.name}</h4>
                    <span className="history-drawer-item-date">
                      {_formatDate(recipe.createdAt)}
                    </span>
                  </div>
                  
                  {recipe.description && (
                    <p className="history-drawer-item-preview">
                      {recipe.description}
                    </p>
                  )}
                  
                  <div className="history-drawer-item-meta">
                    <div className="history-drawer-item-stats">
                      <div className="history-drawer-item-stat">
                        <span>💬</span>
                        <span>{recipe.chatHistory.length} 条对话</span>
                      </div>
                      <div className="history-drawer-item-stat">
                        <span>📅</span>
                        <span>{_formatDetailTime(recipe.createdAt)}</span>
                      </div>
                    </div>
                    
                    <div className="history-drawer-item-actions">
                      {/* <button
                        className="history-drawer-item-action"
                        onClick={(e) => {
                          e.stopPropagation();
                          // 复制配方功能
                          navigator.clipboard.writeText(JSON.stringify(recipe, null, 2));
                          alert('配方已复制到剪贴板');
                        }}
                        title="复制配方"
                        aria-label="复制配方"
                      >
                        📋
                      </button> */}
                      <button
                        className="history-drawer-item-action danger"
                        onClick={(e) => _handleDeleteRecipe(recipe.id, e)}
                        title="删除配方"
                        aria-label="删除配方"
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* 底部操作栏 */}
        {!loading && !error && recipes.length > 0 && (
          <div className="history-drawer-footer">
            {/* <button
              className="history-drawer-footer-btn"
              onClick={_handleExportAll}
              type="button"
            >
              导出全部
            </button> */}
            <button
              className="history-drawer-footer-btn"
              onClick={_handleClearAll}
              type="button"
            >
              清空历史
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default HistoryDrawer;